import React from 'react';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useQuery } from '@apollo/client';
import { DialogHeader } from 'components/shared/Dialog/DialogHeader';
import { CircularProgress, Dialog, DialogContent, Stack } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';

import { ActivityType } from '../../types';
import { DealActivityQuery } from '../../api/types';
import { NoteActivityForm } from '../form/NoteActivityForm';
import { dealActivityApi, useDealActivityMutations } from '../../api';

export const NoteActivityDialog = () => {
  const { handleClose, isOpen, data } = useModal(ModalsKeys.NoteActivityDialog);

  const { data: response, loading } = useQuery<DealActivityQuery>(dealActivityApi.dealActivity, {
    skip: !data?.entityId,
    variables: { input: { id: data.entityId } },
  });

  const { handleCreateDealActivity, handleUpdateDealActivity, isDealActivityCreating, isDealActivityUpdating } =
    useDealActivityMutations();

  const dealActivity = response?.dealActivity?.result?.entity;

  if (dealActivity === undefined || dealActivity?.type !== ActivityType.NOTE) return null;

  const isEditMode = Boolean(dealActivity);

  const handleSubmit = async (formValues: { content: string; subject: string }) => {
    const commonVariables = {
      type: ActivityType.NOTE,
      parentId: data.parentId,
      parentType: data.parentType,
    };

    if (!isEditMode) {
      await handleCreateDealActivity({ ...commonVariables, note: { contactIds: [], ...formValues } });
    } else {
      await handleUpdateDealActivity({ ...commonVariables, id: dealActivity.id, note: formValues });
    }

    handleClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="md">
      <DialogHeader label={isEditMode || loading ? 'Edit Note' : 'Add New Note'} handleClose={handleClose} />

      <DialogContent>
        <Stack sx={{ paddingTop: '0.5rem' }}>
          <RenderWithFallback
            isLoading={loading}
            fallBack={
              <Stack height="240px" alignItems="center" justifyContent="center">
                <CircularProgress />
              </Stack>
            }
          >
            <NoteActivityForm
              isEdit={isEditMode}
              onCancel={handleClose}
              onSubmit={handleSubmit}
              isSubmitting={isDealActivityCreating || isDealActivityUpdating}
              initialValues={dealActivity ? { subject: dealActivity.topic, content: dealActivity.content } : null}
            />
          </RenderWithFallback>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
